import { PaletteOptions, alpha } from '@mui/material/styles';
import { renderThemes } from './themes';
import { grey, Themes } from './themeTypes';

export interface ExtendedPaletteOptions {
  dialog_background: string;
  dialog_title: string;
  dialog_text: string;
  tooltip_color: string;
  data_table_header: string;
  data_table_body: string;
  data_header_text: string;
  data_table_rollover: string;
  data_table_text: string;
  calendar_current_day: string;
  calendar_weekend: string;
  calendar_main: string;
  calendar_line: string;
  calendar_week_heading: string;
  calendar_text: string;
  calendar_text_hover: string;
  accordion_color: string;
  secondary_component_hover: string;
  impersonate_color: string;
  primary_component_border: string;
  disabled_color: string;
  text_color: string;
  top_nav_text_color: string;
  checkpoint_question: string;
  contained_text: string;
}

export function createPalette(theme = Themes.LIGHT): PaletteOptions {
  let resolvedTheme = theme;

  // Handle SYSTEM theme resolution - ensure we never pass SYSTEM to renderThemes
  if (Number(theme) === Themes.SYSTEM) {
    if (typeof window !== 'undefined') {
      // Browser environment - use matchMedia
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      resolvedTheme = prefersDark ? Themes.DARK : Themes.LIGHT;
    } else {
      // Server environment or initialization - default to light theme
      resolvedTheme = Themes.LIGHT;
    }
  }

  // Ensure we only pass LIGHT or DARK to renderThemes
  const finalTheme = (resolvedTheme === Themes.LIGHT || resolvedTheme === Themes.DARK)
    ? resolvedTheme
    : Themes.LIGHT;

  const themePalette = renderThemes(finalTheme);
  return {
    action: {
      active: themePalette.secondary,
      disabled: alpha(themePalette.primary, 0.38),
      disabledBackground: alpha(themePalette.primary, 0.12),
      focus: alpha(themePalette.primary, 0.16),
      hover: alpha(themePalette.primary, 0.04),
      selected: alpha(themePalette.primary, 0.12),
    },
    dialog_background: themePalette.dialog_background,
    dialog_title: themePalette.dialog_title,
    dialog_text: themePalette.dialog_text,
    primary_component_border: themePalette.primary_component_border,
    tooltip_color: themePalette.tooltip_color,
    text_color: themePalette.text_color,
    background: {
      default: themePalette.default_background,
    },
    divider: themePalette.divider,
    error: themePalette.error,
    info: themePalette.info,
    mode: resolvedTheme === Themes.DARK ? 'dark' : 'light',
    grey,
    primary: themePalette.primary_component,
    success: themePalette.success,
    data_table_header: themePalette.data_table_header,
    data_header_text: themePalette.data_header_text,
    data_table_body: themePalette.data_table_body,
    data_table_rollover: themePalette.data_table_rollover,
    data_table_text: themePalette.data_table_text,
    calendar_current_day: themePalette.calendar_current_day,
    calendar_weekend: themePalette.calendar_weekend,
    calendar_main: themePalette.calendar_main,
    top_nav_text_color: themePalette.top_nav_text_color,
    calendar_line: themePalette.calendar_line,
    calendar_week_heading: themePalette.calendar_week_heading,
    calendar_text: themePalette.calendar_text,
    calendar_text_hover: themePalette.calendar_text_hover,
    accordion_color: themePalette.accordion_color,
    secondary_component_hover: themePalette.secondary_component_hover,
    impersonate_color: themePalette.impersonate_color,
    disabled_color: themePalette.disabled_color,
    checkpoint_question: themePalette.checkpoint_question,
    contained_text: themePalette.contained_text,

    text: {
      primary: themePalette.text_color,
      secondary: themePalette.secondary,
      disabled: themePalette.disabled_color,
    },
    warning: themePalette.warning,
  };
}

// Define custom properties in the Palette interface
declare module '@mui/material/styles' {
  interface Palette extends ExtendedPaletteOptions { } // Extend the base Palette

  interface PaletteOptions extends ExtendedPaletteOptions { } // Extend the options for theme creation
}
